import { Component } from "@theme/component";
import { StandardEvents } from "@shopify/events";
import { DrawerOpenEvent } from "@theme/theme-drawer";
class CartDrawerComponent extends Component {
  #summaryThreshold = 0.5;
  #cartUpdateGeneration = 0;
  #layoutFrame = 0;
  #layoutObserver = null;
  #layoutDialog = null;
  #observedSummary = null;
  #visualViewport = null;
  #keyboardVisible = false;
  get #themeDrawer() {
    return this.closest("theme-drawer");
  }
  get #dialog() {
    return this.closest("dialog");
  }
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate,
    );
    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen,
    );
    this.#layoutDialog = this.#dialog;
    this.#visualViewport = window.visualViewport;
    window.addEventListener("resize", this.#scheduleLayout, { passive: true });
    this.#visualViewport?.addEventListener("resize", this.#scheduleLayout, {
      passive: true,
    });
    this.#visualViewport?.addEventListener("scroll", this.#scheduleLayout, {
      passive: true,
    });
    this.addEventListener("focusin", this.#scheduleLayout);
    this.addEventListener("focusout", this.#scheduleLayout);
    this.addEventListener("toggle", this.#scheduleLayout, true);
    this.#layoutDialog?.addEventListener("close", this.#scheduleLayout);
    if (typeof ResizeObserver !== "undefined") {
      this.#layoutObserver = new ResizeObserver(this.#scheduleLayout);
      if (this.#layoutDialog) this.#layoutObserver.observe(this.#layoutDialog);
    }
    this.#updateStickyState();
    if (this.#themeDrawer?.hasAttribute("open")) this.#handleDrawerOpen();
  }
  disconnectedCallback() {
    (super.disconnectedCallback(),
      document.removeEventListener(
        StandardEvents.cartLinesUpdate,
        this.#handleCartLinesUpdate,
      ),
      this.#themeDrawer?.removeEventListener(
        DrawerOpenEvent.eventName,
        this.#handleDrawerOpen,
      ));
    window.removeEventListener("resize", this.#scheduleLayout);
    this.#visualViewport?.removeEventListener("resize", this.#scheduleLayout);
    this.#visualViewport?.removeEventListener("scroll", this.#scheduleLayout);
    this.removeEventListener("focusin", this.#scheduleLayout);
    this.removeEventListener("focusout", this.#scheduleLayout);
    this.removeEventListener("toggle", this.#scheduleLayout, true);
    this.#layoutDialog?.removeEventListener("close", this.#scheduleLayout);
    this.#layoutObserver?.disconnect();
    this.#layoutObserver = null;
    this.#observedSummary = null;
    cancelAnimationFrame(this.#layoutFrame);
    this.#layoutFrame = 0;
    this.#resetViewport();
    this.#layoutDialog = null;
    this.#visualViewport = null;
  }
  #handleDrawerOpen = () => {
    this.#scheduleLayout();
    (this.#updateStickyState(),
      customElements.whenDefined("shopify-payment-terms").then(() => {
        this.querySelector("shopify-payment-terms")
          ?.shadowRoot?.querySelector("#shopify-installments-cta")
          ?.addEventListener("click", () => this.#themeDrawer?.close(), {
            once: !0,
          });
      }));
  };
  #handleCartLinesUpdate = (event) => {
    const generation = ++this.#cartUpdateGeneration,
      ui = event.tcCartUi,
      shouldAutoOpen =
        this.hasAttribute("auto-open") &&
        event.action === "add" &&
        !this.#themeDrawer?.isOpen,
      sourceModal =
        event.target instanceof Element
          ? event.target.closest("dialog:modal")
          : null;
    (!ui &&
      shouldAutoOpen &&
      !sourceModal &&
      !this.#isCartEmpty() &&
      this.#themeDrawer?.open(),
      (ui?.promise ?? event.promise)
        ?.then(async ({ detail }) => {
          const settle = () =>
            requestAnimationFrame(() => this.#updateStickyState());
          if (
            detail?.didError ||
            detail?.stale ||
            generation !== this.#cartUpdateGeneration
          ) {
            settle();
            return;
          }
          if (ui) {
            const rendered = await Promise.allSettled(ui.renderTasks);
            if (
              generation !== this.#cartUpdateGeneration ||
              rendered.some((result) => result.status === "rejected")
            )
              return;
          }
          if (!shouldAutoOpen) {
            settle();
            return;
          }
          const openAndSettle = () => {
            generation === this.#cartUpdateGeneration &&
              (this.#themeDrawer?.isOpen || this.#themeDrawer?.open(),
              settle());
          };
          sourceModal?.open
            ? sourceModal.addEventListener("close", openAndSettle, { once: !0 })
            : openAndSettle();
        })
        .catch((error) => {
          error?.name !== "AbortError" &&
            console.warn("[cart-drawer] Event promise rejected:", error);
        }));
  };
  #isCartEmpty() {
    return !!this.querySelector(".cart-drawer--empty");
  }
  #scheduleLayout = () => {
    if (this.#layoutFrame || !this.isConnected) return;
    this.#layoutFrame = requestAnimationFrame(() => {
      this.#layoutFrame = 0;
      if (!this.isConnected) return;
      this.#syncViewport();
      this.#updateStickyState();
      this.#revealFocusedField();
    });
  };

  #resetViewport() {
    this.#keyboardVisible = false;
    this.#layoutDialog?.style.removeProperty("--cart-viewport-height");
    this.#layoutDialog?.style.removeProperty("--cart-viewport-top");
  }

  #syncViewport() {
    const dialog = this.#layoutDialog;
    const viewport = this.#visualViewport;
    // Pinch zoom keeps native browser behavior. Keyboard handling is local to this drawer.
    if (!dialog?.open || !viewport || Math.abs(viewport.scale - 1) > 0.01) {
      this.#resetViewport();
      return;
    }
    const focused =
      this.contains(document.activeElement) &&
      document.activeElement?.matches(
        "input:not([type='hidden']), textarea, select",
      );
    const height = Math.round(viewport.height);
    const occluded = window.innerHeight - height;
    if (
      !Number.isFinite(height) ||
      height <= 0 ||
      occluded <= 80 ||
      (!focused && !this.#keyboardVisible)
    ) {
      this.#resetViewport();
      return;
    }
    this.#keyboardVisible = true;
    const top = Math.max(
      0,
      Math.min(viewport.offsetTop || 0, window.innerHeight - height),
    );
    for (const [name, value] of [
      ["--cart-viewport-height", `${height}px`],
      ["--cart-viewport-top", `${Math.round(top)}px`],
    ]) {
      if (dialog.style.getPropertyValue(name) !== value)
        dialog.style.setProperty(name, value);
    }
  }

  #revealFocusedField() {
    const active = document.activeElement;
    if (
      !this.#keyboardVisible ||
      !this.contains(active) ||
      !active?.matches("input:not([type='hidden']), textarea, select")
    )
      return;
    const field = active.closest(".cart-discount__form") || active;
    const rect = field.getBoundingClientRect();
    const top = this.#visualViewport.offsetTop || 0;
    const bottom = top + this.#visualViewport.height;
    if (rect.bottom > bottom - 12 || rect.top < top + 12) {
      field.scrollIntoView({
        block: "nearest",
        inline: "nearest",
        behavior: "instant",
      });
    }
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;
    const content = dialog.querySelector(".cart-drawer__content"),
      summary = dialog.querySelector(".cart-drawer__summary");
    if (summary !== this.#observedSummary) {
      if (this.#observedSummary)
        this.#layoutObserver?.unobserve(this.#observedSummary);
      this.#observedSummary = summary;
      if (summary) this.#layoutObserver?.observe(summary);
    }
    if (!content || !summary) {
      dialog.setAttribute("cart-summary-sticky", "false");
      return;
    }
    const drawerHeight = dialog.getBoundingClientRect().height;
    if (drawerHeight <= 0) return;
    const ratio = summary.getBoundingClientRect().height / drawerHeight;
    const sticky = ratio > this.#summaryThreshold ? "false" : "true";
    if (dialog.getAttribute("cart-summary-sticky") !== sticky) {
      dialog.setAttribute("cart-summary-sticky", sticky);
    }
  }
}
customElements.get("cart-drawer-component") ||
  customElements.define("cart-drawer-component", CartDrawerComponent);
